
import React, { useRef, useState } from "react";
import { HiOutlinePhotograph, HiOutlineSparkles } from "react-icons/hi";
import { PiSmileyFill } from "react-icons/pi";

import PostButton from "../PostButton";
import ToastMessage from "../ToastMessage";
import FileGridUploader from "../FileGridUpload";
import EmojiReactPicker from "../EmojiReactPicker";
import { useUploads } from "../../context/UploadContext";
import { compressImages } from "../../lib/compressImage";

const Photo = ({ close }) => {
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const smileyRef = useRef(null);
  const { startUpload } = useUploads();

  const type = "photo";
  const maxCaptionLength = 500;
  const maxFiles = 10;
  const maxFileSize = 15 * 1024 * 1024;
  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];

  
const handleSubmit = async (e) => {
  e?.preventDefault?.();
  if (isSubmitting) return;

  if (!files.length) return ToastMessage("Please add at least one image.");
  if (files.length > 10) return ToastMessage("Maximum 10 photos per post.");

  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
  const maxFileSize = 15 * 1024 * 1024;

  for (const { file } of files) {
    if (!file || !allowedTypes.includes(file.type)) return ToastMessage(`Unsupported image: ${file?.name || "Unknown"}`);
    if (file.size > maxFileSize) return ToastMessage(`${file.name} exceeds 15 MB.`);
  }

  setIsSubmitting(true);

  try {
    const originals = files.map(({ file }) => file);
    const result = await compressImages(originals, { maxWidth: 1920, maxHeight: 1920, quality: 0.82 });

    const formData = new FormData();
    formData.append("caption", caption.trim());
    formData.append("type", "photo");
    result.files.forEach((file) => formData.append("content", file));

    startUpload({
      formData,
      name: result.files.length === 1 ? result.files[0].name : `${result.files.length} photos`,
    });

    console.log("Alakoze image optimization:", {
      original: `${result.originalMB} MB`,
      compressed: `${result.compressedMB} MB`,
      saved: `${result.savedPercent}%`,
    });

    setFiles([]);
    setCaption("");
    setShowEmoji(false);
    close?.();

    ToastMessage(result.savedPercent > 0 ? `Photos optimized (${result.savedPercent}% smaller). Uploading...` : "Photos uploading in the background.");
  } catch (error) {
    console.error("Photo preparation failed:", error);
    ToastMessage("Unable to prepare photos for upload.");
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <div className="w-full overflow-x-hidden">
      <form onSubmit={handleSubmit}>
        {/* ================= PHOTO HEADER ================= */}

        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <HiOutlinePhotograph size={21} />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900">Photo post</h3>
              <p className="mt-0.5 text-xs text-gray-400">Share up to 10 photos</p>
            </div>
          </div>

          {files.length > 0 && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">{files.length}/{maxFiles}</span>}
        </div>

        {/* ================= FILE UPLOAD ================= */}

        <div className="relative overflow-visible rounded-2xl border border-gray-200 bg-gray-50/40 p-2 transition-all duration-200 hover:border-gray-300">
          <FileGridUploader allowMultiple maxFiles={maxFiles} files={files} setFiles={setFiles} acceptedFileTypes="image/*" />
        </div>

        {/* ================= UPLOAD INFO ================= */}

        {files.length === 0 && (
          <div className="mt-2 flex items-center gap-1.5 px-1 text-[11px] text-gray-400">
            <HiOutlineSparkles size={14} className="text-purple-400" />
            JPG, PNG and other supported image formats
          </div>
        )}

        {/* ================= CAPTION ================= */}

        <div className="mt-5 overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-200 focus-within:border-purple-300 focus-within:ring-4 focus-within:ring-purple-50">
          <div className="relative">
            <label htmlFor="photo-caption" className="sr-only">Photo caption</label>

            <textarea
              id="photo-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={4}
              maxLength={maxCaptionLength}
              placeholder="Add a caption..."
              className="min-h-[105px] w-full resize-none border-none bg-white px-4 py-3.5 pr-12 text-sm leading-6 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
            />

            {/* Emoji Button */}

            <button
              ref={smileyRef}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowEmoji((prev) => !prev);
              }}
              className={`absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200 ${showEmoji ? "bg-purple-50 text-purple-600" : "bg-gray-50 text-gray-400 hover:bg-purple-50 hover:text-purple-600"}`}
            >
              <PiSmileyFill size={20} />
            </button>

            <EmojiReactPicker set={setCaption} showEmoji={showEmoji} setShowEmoji={setShowEmoji} triggerRef={smileyRef} />
          </div>

          {/* Caption Footer */}

          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-4 py-2">
            <span className="text-[11px] text-gray-400">Caption is optional</span>
            <span className={`text-[11px] font-medium ${caption.length > maxCaptionLength * 0.9 ? "text-orange-500" : "text-gray-400"}`}>{caption.length}/{maxCaptionLength}</span>
          </div>
        </div>

        {/* ================= POST ACTION ================= */}

        <div className="mt-4 flex items-center justify-between">
          <p className="hidden text-xs text-gray-400 sm:block">
            {files.length > 0 ? `${files.length} ${files.length === 1 ? "photo" : "photos"} ready to publish` : "Add photos to continue"}
          </p>

          <div className={`ml-auto transition-all duration-200 ${files.length > 0 ? "opacity-100" : "pointer-events-none opacity-50"}`}>
            <PostButton type="submit" disabled={files.length === 0 || isSubmitting} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default Photo;
